import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiDownload } from 'react-icons/fi';
import BottomTabMenu from '../../components/UI/BottomTabMenu';
import { LoadingGrid } from '../../components/UI/Loading';
import { vendedoresService } from '../../services';
import { exportToCsv, exportToPdf } from '../../utils/exportReport';

const VendedorComisiones = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [comisiones, setComisiones] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadComisiones();
  }, [id]);

  const loadComisiones = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await vendedoresService.getComisionesVendedor(id);

      if (result.success) {
        // Transformar los datos de la API
        const comisionesData = result.data.map((c, index) => ({
          id: c.invoice_id ?? `com-${index}`,
          fecha: c.invoice_date || '',
          periodo: c.period || '',
          factura: c.invoice_number || c.sale_number || '—',
          cliente: c.client_name || '—',
          ventas: parseFloat(c.sales_amount || 0),
          porcentaje: parseFloat(c.commission_percentage || 0),
          comision: parseFloat(c.commission_amount || 0)
        }));
        setComisiones(comisionesData);
      } else {
        setError(result.message || 'Error al cargar las comisiones');
        setComisiones([]);
      }
    } catch (err) {
      console.error('Error cargando comisiones:', err);
      setError('Error de conexión al cargar las comisiones');
      setComisiones([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = (format) => {
    if (comisiones.length === 0) {
      alert('No hay comisiones para exportar.');
      return;
    }
    const columns = [
      { label: 'Fecha', value: (c) => formatDate(c.fecha) },
      { label: 'Factura', value: (c) => c.factura },
      { label: 'Cliente', value: (c) => c.cliente },
      { label: 'Venta (facturado pagado)', value: (c) => formatCurrency(c.ventas) },
      { label: '% Comisión', value: (c) => `${c.porcentaje}%` },
      { label: 'Comisión', value: (c) => formatCurrency(c.comision) }
    ];
    const stamp = new Date().toISOString().split('T')[0];
    const fileBase = `comisiones_vendedor_${id}_${stamp}`;
    if (format === 'csv') {
      exportToCsv(columns, comisiones, fileBase);
    } else {
      exportToPdf('Reporte de Comisiones', columns, comisiones, `Vendedor #${id}`);
    }
  };

  const formatCurrency = (n) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2
    }).format(n || 0);

  // Formatea YYYY-MM-DD a DD/MM/YYYY sin desfase de zona horaria
  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const m = String(dateString).match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : dateString;
  };

  const totalVentas = comisiones.reduce((s, c) => s + c.ventas, 0);
  const totalComision = comisiones.reduce((s, c) => s + c.comision, 0);

  if (isLoading) {
    return <LoadingGrid message="Cargando reporte de comisiones..." />;
  }

  return (
    <div className="vendedor-comisiones-page">
      {/* Error message */}
      {error && (
        <div className="alert alert--danger" style={{ margin: '20px' }}>
          <strong>Error:</strong> {error}
          <button
            className="btn btn--ghost btn--sm"
            onClick={loadComisiones}
            style={{ marginLeft: '10px' }}
          >
            Reintentar
          </button>
        </div>
      )}

      {/* Header */}
      <div className="form-header">
        <button
          className="btn btn--ghost"
          onClick={() => navigate('/system/vendedores')}
          type="button"
        >
          <FiArrowLeft /> Volver a Vendedores
        </button>

        <h1 className="form-title">
          REPORTE DE COMISIONES
        </h1>

        <div className="form-actions">
          <button className="btn btn--outline" onClick={() => handleExport('csv')} type="button">
            <FiDownload /> CSV
          </button>
          <button className="btn btn--primary" onClick={() => handleExport('pdf')} type="button">
            <FiDownload /> PDF
          </button>
        </div>
      </div>

      {/* Contenido */}
      <div className="page-content">
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>FECHA</th>
                <th>FACTURA</th>
                <th>CLIENTE</th>
                <th className="text-right">VENTA (FACTURADO PAGADO)</th>
                <th className="text-center">%</th>
                <th className="text-right">COMISIÓN</th>
              </tr>
            </thead>
            <tbody>
              {comisiones.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center" style={{ padding: '24px', color: '#6b7280' }}>
                    Sin comisiones: este vendedor no tiene facturas pagadas de sus ventas.
                  </td>
                </tr>
              ) : (
                comisiones.map((comision) => (
                  <tr key={comision.id}>
                    <td>{formatDate(comision.fecha)}</td>
                    <td><strong>{comision.factura}</strong></td>
                    <td>{comision.cliente}</td>
                    <td className="text-right">{formatCurrency(comision.ventas)}</td>
                    <td className="text-center">
                      <span className="badge badge-warning">{comision.porcentaje}%</span>
                    </td>
                    <td className="text-right">
                      <strong style={{ color: '#28a745' }}>{formatCurrency(comision.comision)}</strong>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {comisiones.length > 0 && (
              <tfoot>
                <tr>
                  <td colSpan="3"><strong>TOTAL</strong></td>
                  <td className="text-right"><strong>{formatCurrency(totalVentas)}</strong></td>
                  <td></td>
                  <td className="text-right"><strong style={{ color: '#28a745' }}>{formatCurrency(totalComision)}</strong></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Menú inferior */}
      <BottomTabMenu
        baseRoute={`/system/vendedores/${id}`}
        currentTab="comisiones"
        tabs={[
          { id: 'datos', label: 'Datos', route: '' },
          { id: 'ventas', label: 'Reporte de Ventas', route: '/ventas' },
          { id: 'comisiones', label: 'Reporte de Comisiones', route: '/comisiones' },
          { id: 'clientes', label: 'Lista de Clientes', route: '/clientes' },
          { id: 'grafica', label: 'Gráfica de Ventas', route: '/grafica' },
          { id: 'notas', label: 'Notas', route: '/notas' }
        ]}
      />
    </div>
  );
};

export default VendedorComisiones;
